import { CheckOutlined } from '@ant-design/icons';
import { type ComunicazioneDTO } from '@framework/api-client/api/inquiries-compile/schemas/comunicazioneDTO.ts';
import { type Destinatario } from '@framework/api-client/api/inquiries-compile/schemas/destinatario.ts';
import { getNetworkErrorMessage } from '@framework/ui/core/errors/getNetworkErrorMessage.tsx';
import { Button, message } from 'antd';
import type * as React from 'react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCommunicationsContext } from '../context/CommunicationsContext.tsx';

export interface IMarkCommunicationAsReadButtonProps {
  communication: ComunicazioneDTO;
  /** Destinatario a cui riferire lo stato di lettura (calcolato dal parent) */
  recipient: Destinatario | undefined;
  /** Il comando riguarda solo le comunicazioni ricevute */
  isCurrentUserSender: boolean;
  isDraft: boolean;
}

/**
 * Comando di presa lettura di una comunicazione ricevuta. Compare solo
 * dove il contesto fornisce `onMarkAsRead` e finché il destinatario
 * corrente non risulta letto.
 */
export function MarkCommunicationAsReadButton(
  props: IMarkCommunicationAsReadButtonProps,
) {
  const { communication, recipient, isCurrentUserSender, isDraft } = props;
  const { t } = useTranslation();
  const { onMarkAsRead } = useCommunicationsContext();
  const [isLoading, setIsLoading] = useState(false);

  const handleClick = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      // Il bottone viene renderizzato solo quando gli early return sotto
      // garantiscono `onMarkAsRead` e `communication.id` presenti
      if (!onMarkAsRead || !communication.id) return;
      setIsLoading(true);
      try {
        await onMarkAsRead(communication.id);
      } catch (error) {
        // Difensivo: il contratto del context non garantisce che
        // `onMarkAsRead` gestisca internamente i propri errori
        console.error(error);
        message.error(
          getNetworkErrorMessage(
            error,
            t(
              'communication-item.mark-as-read-error',
              'Impossibile segnare la comunicazione come letta',
            ),
          ),
        );
      } finally {
        setIsLoading(false);
      }
    },
    [onMarkAsRead, communication.id, t],
  );

  if (!onMarkAsRead || isDraft || isCurrentUserSender || !communication.id) {
    return null;
  }

  // Mostriamo il comando solo con uno stato di lettura esplicito: se il
  // destinatario non viene individuato o `flLetta` è assente, la presa
  // lettura non è applicabile
  if (recipient?.flLetta !== false) return null;

  return (
    <Button
      icon={<CheckOutlined />}
      size="middle"
      loading={isLoading}
      onClick={handleClick}
    >
      {t('communication-item.mark-as-read', 'Segna come letta')}
    </Button>
  );
}
